import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LANGUAGE,
  normalizeSupportedLanguageCodes,
  getLocaleUrl,
  getLocaleUrlRefresher,
  getSessionCache,
  setSessionCache,
  clearSessionCache,
} from "./languages";
import enTranslation from "./locales/en/translation.json";

export { registerLocaleUrls, setLocaleUrlRefresher } from "./languages";

const loadedLanguageResources = new Set<string>();

export const ensureLanguageResourcesLoaded = async (
  language: string,
): Promise<void> => {
  if (loadedLanguageResources.has(language)) {
    return;
  }

  // 1. Check session storage (S3-fetched in a prior call this session)
  const cached = getSessionCache(language);
  if (cached) {
    i18n.addResourceBundle(language, "translation", cached, true, true);
    loadedLanguageResources.add(language);
    return;
  }

  // 2. Fetch from S3 using the registered URL (populated from /locales API)
  const url = getLocaleUrl(language);
  if (url) {
    await loadLanguageFromUrl(language, url);
    return;
  }

  // 3. Last resort: only for English, fall back to the bundled codebase file
  if (language === DEFAULT_LANGUAGE) {
    i18n.addResourceBundle(language, "translation", enTranslation, true, true);
    loadedLanguageResources.add(language);
  }
};

// Initialize synchronously at module load with the bundled English translation.
// NOTE: English is intentionally NOT added to loadedLanguageResources here so that
// ensureLanguageResourcesLoaded will still fetch the S3 version after authentication.
void i18n.use(initReactI18next).init({
  resources: {
    [DEFAULT_LANGUAGE]: { translation: enTranslation },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [DEFAULT_LANGUAGE],
  interpolation: {
    escapeValue: false,
  },
});

const applyAndCacheTranslation = (
  language: string,
  translation: unknown,
): void => {
  i18n.addResourceBundle(language, "translation", translation, true, true);
  loadedLanguageResources.add(language);
  setSessionCache(language, translation);
};

const fetchAndApplyTranslation = async (
  language: string,
  url: string,
): Promise<void> => {
  try {
    const response = await fetch(url);

    // S3 presigned URLs return 403 when expired — refresh URLs and retry once
    if (response.status === 403) {
      const refresher = getLocaleUrlRefresher();
      if (!refresher) {
        console.error(
          `Translation URL for "${language}" has expired and no refresher is registered.`,
        );
        return;
      }
      clearSessionCache(language);
      await refresher();
      const freshUrl = getLocaleUrl(language);
      if (!freshUrl) {
        console.error(
          `No URL available for "${language}" after refreshing locale URLs.`,
        );
        return;
      }
      const retryResponse = await fetch(freshUrl);
      if (!retryResponse.ok) {
        console.error(
          `Failed to fetch translations for "${language}" after URL refresh: ${retryResponse.status}`,
        );
        return;
      }
      const translation: unknown = await retryResponse.json();
      applyAndCacheTranslation(language, translation);
      return;
    }

    if (!response.ok) {
      console.error(
        `Failed to fetch translations for "${language}": ${response.status}`,
      );
      return;
    }
    const translation: unknown = await response.json();
    applyAndCacheTranslation(language, translation);
  } catch (error) {
    console.error(`Unexpected error loading translations for "${language}":`, error);
  }
};

export const loadLanguageFromUrl = async (
  language: string,
  url: string,
): Promise<void> => {
  if (loadedLanguageResources.has(language)) {
    return;
  }

  const cached = getSessionCache(language);
  if (cached) {
    i18n.addResourceBundle(language, "translation", cached, true, true);
    loadedLanguageResources.add(language);
    return;
  }

  await fetchAndApplyTranslation(language, url);
};

export const updateSupportedLanguages = (languages: string[]) => {
  const normalized = normalizeSupportedLanguageCodes(languages);
  const languageUtils = i18n.services?.languageUtils as
    | { supportedLngs: string[] | false }
    | undefined;
  if (languageUtils) {
    languageUtils.supportedLngs = normalized;
  }
  // Keep i18n.options in sync so applyLanguagePriority and other code
  // reading i18n.options.supportedLngs see the updated list.
  i18n.options.supportedLngs = normalized;
};

export default i18n;
